/* ============================================================
   EXON theme runtime (Track A0)
   · event bus — one channel for every island (`exon:*` events)
   · cart store — Shopify cart is the source of truth (/cart.js);
     every mutation goes through here and broadcasts `exon:cart`
   · overlay manager — scroll lock, esc, stacking, focus return
   No framework. Islands subscribe; Liquid renders first paint.
   ============================================================ */
(function () {
  'use strict';

  /* ---------------- event bus ---------------- */
  const bus = {
    on(type, fn) { document.addEventListener(type, fn); return () => document.removeEventListener(type, fn); },
    emit(type, detail) { document.dispatchEvent(new CustomEvent(type, { detail })); },
  };

  /* shallow-clone a cart line's existing line-item properties by line key, from
     the last /cart.js snapshot. Used to make upgrade/revert PROPERTY-PRESERVING:
     Shopify line identity includes properties, so dropping them on a re-add both
     loses attribution (e.g. _exon_plc_token) and can split an otherwise-merging
     line. Generic — preserves every property, not just ours. */
  function linePropsByKey(key) {
    try {
      const items = (cart.state && cart.state.items) || [];
      for (let i = 0; i < items.length; i++) {
        if (items[i] && items[i].key === key) {
          const p = items[i].properties;
          return (p && typeof p === 'object') ? Object.assign({}, p) : {};
        }
      }
    } catch (e) { /* no snapshot — fall through to none */ }
    return {};
  }

  function sameGiftLine(line, identity) {
    if (!line || !identity) return false;
    const p = (line.properties && typeof line.properties === 'object') ? line.properties : {};
    if (identity.launchRewardIssueId) {
      return String(line.variant_id || line.id || '') === String(identity.variantId || '') &&
        String(p._exon_launch_reward_issue_id || '') === String(identity.launchRewardIssueId) &&
        String(p._exon_launch_campaign_version_id || '') === String(identity.campaignVersionId || '') &&
        String(p._exon_launch_variant_gid || '') === String(identity.variantGid || '');
    }
    if (identity.choiceGrantId) {
      return String(line.variant_id || line.id || '') === String(identity.variantId || '') &&
        String(p._exon_choice_grant_id || '') === String(identity.choiceGrantId) &&
        String(p._exon_choice_variant_gid || '') === String(identity.variantGid || '');
    }
    return String(line.variant_id || line.id || '') === String(identity.variantId || '') &&
      String(p._exon_gift_entitlement_id || '') === String(identity.entitlementId || '') &&
      String(p._exon_gift_campaign_version_id || '') === String(identity.campaignVersionId || '') &&
      String(p._exon_gift_variant_gid || '') === String(identity.variantGid || '');
  }

  // Ajax exposes discount applications, not their original input codes.
  // Only an automatic application can survive a code replacement safely.
  function hasNonAutomaticDiscount(state) {
    if (!state || !Array.isArray(state.items)) return true;
    function unsafe(applications) {
      return !Array.isArray(applications) || applications.some((application) =>
        !application || application.type !== 'automatic');
    }
    if (unsafe(state.cart_level_discount_applications || [])) return true;
    for (const line of state.items) {
      const allocations = line && line.line_level_discount_allocations;
      if (allocations != null && !Array.isArray(allocations)) return true;
      if (unsafe((allocations || []).map((allocation) => allocation && allocation.discount_application))) return true;
    }
    return false;
  }

  // Opaque Ajax application keys identify the effects of a completed request;
  // they are never reconstructed into discount-code inputs.
  function discountApplicationKeys(state) {
    if (!state || !Array.isArray(state.items)) return null;
    const applications = (state.cart_level_discount_applications || []).slice();
    for (const line of state.items) {
      for (const allocation of line.line_level_discount_allocations || []) {
        applications.push(allocation && allocation.discount_application);
      }
    }
    const keys = [];
    for (const application of applications) {
      if (application && application.type === 'automatic') continue;
      if (!application || typeof application.key !== 'string' || !application.key) return null;
      if (!keys.includes(application.key)) keys.push(application.key);
    }
    return keys.sort().join('\n');
  }

  /* ---------------- cart store ---------------- */
  const cart = {
    state: null,
    /* the in-flight boot /cart.js refresh — lets consumers (e.g. the PDP
       attribution carrier) await the FIRST snapshot instead of racing it. */
    ready: null,

    async refresh() {
      const res = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
      cart.state = await res.json();
      bus.emit('exon:cart', cart.state);
      return cart.state;
    },

    async add(items, opts = {}) {
      // items: [{ id: variantId, quantity, properties? }]
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: Array.isArray(items) ? items : [items] }),
      });
      if (!res.ok) throw await res.json().catch(() => new Error('add failed'));
      await cart.refresh();
      if (!opts.silent) bus.emit('exon:cart:added', { items });
      return cart.state;
    },

    async change(lineKeyOrIndex, quantity, properties) {
      const body = { quantity };
      if (typeof lineKeyOrIndex === 'string') body.id = lineKeyOrIndex;
      else body.line = lineKeyOrIndex;
      if (properties) body.properties = properties;
      const res = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) throw await res.json().catch(() => new Error('change failed'));
      await cart.refresh();
      return cart.state;
    },

    /* upgrade = swap line to another variant, remembering the original in
       line-item properties so the drawer's undo survives re-render. Carries the
       outgoing line's OTHER properties across (attribution etc.); upgrade owns
       _original_variant, so it wins over any carried key of that name.
       ADD-FIRST: the target goes in before the original comes out, so a failed
       add leaves the customer's line untouched; a failed removal takes the
       just-added target back out — success never resolves unless both ops did. */
    async upgrade(lineKey, toVariantId, qty, originalVariantId) {
      const carried = linePropsByKey(lineKey);
      const props = Object.assign({}, carried, { _original_variant: String(originalVariantId) });
      await cart.add({ id: toVariantId, quantity: qty, properties: props }, { silent: true });
      try {
        return await cart.change(lineKey, 0);
      } catch (err) {
        /* compensate: the customer must never end up holding both lines. Find
           OUR just-added line (target variant, preferring our undo stamp) in a
           fresh snapshot and remove it, then surface the original failure. */
        try {
          const state = await cart.refresh();
          const items = (state && state.items) || [];
          let target = null;
          for (let i = 0; i < items.length; i++) {
            const it = items[i];
            if (String(it.variant_id || it.id) !== String(toVariantId)) continue;
            if (it.properties && it.properties._original_variant === String(originalVariantId)) { target = it; break; }
            if (!target) target = it; // /cart.js lists newest first
          }
          if (target) await cart.change(target.key, 0);
        } catch (e) { /* best-effort — the reject below still fires */ }
        throw err;
      }
    },

    async revert(lineKey, qty, originalVariantId) {
      const carried = linePropsByKey(lineKey);
      delete carried._original_variant; /* undo marker doesn't survive the revert */
      /* nor does offer attribution — the restored line is the DECLINED state,
         and a stale accept tuple would mis-attribute it as a conversion. The
         tuple is NOT all '_exon_offer_*': it also spans _exon_relevance_tier,
         _exon_axis, _exon_position, … (upsell.js offerAcceptProps), so strip
         every '_exon_'-prefixed key EXCEPT the placement carrier
         ('_exon_plc_*', product.js plcProps) — that attribution belongs to
         the original PDP add being restored, not to the declined offer. */
      for (const k of Object.keys(carried)) {
        if (k.indexOf('_exon_') === 0 && k.indexOf('_exon_plc_') !== 0) delete carried[k];
      }
      await cart.change(lineKey, 0);
      const item = { id: Number(originalVariantId), quantity: qty };
      if (Object.keys(carried).length) item.properties = carried;
      return cart.add(item, { silent: true });
    },

    async setAttributes(attributes) {
      const res = await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ attributes }),
      });
      if (!res.ok) throw new Error('attributes failed');
      await cart.refresh();
      return cart.state;
    },

    /* Exact Gift ownership is a four-part identity. Variant-only lookup is
       forbidden because a paid copy of the same SKU may already be in cart. */
    giftLines(identity, state) {
      const items = ((state || cart.state) && (state || cart.state).items) || [];
      return items.filter((line) => sameGiftLine(line, identity));
    },

    hasNonAutomaticDiscount,

    /* Do not replace an existing customer code: Ajax cannot recover its input.
       Recheck immediately before this Gift-only mutation as well as before add. */
    async applyDiscount(code, attempt) {
      const wanted = String(code || '').trim();
      if (!wanted || /[,\r\n]/.test(wanted)) throw Object.assign(new Error('discount_code_missing'), { code: 'discount_code_missing' });
      const before = await cart.refresh();
      if (!before || !Array.isArray(before.items)) {
        throw Object.assign(new Error('cart_state_unknown'), { code: 'cart_state_unknown' });
      }
      if (hasNonAutomaticDiscount(before)) {
        throw Object.assign(new Error('existing_discount'), { code: 'existing_discount' });
      }
      if (attempt) attempt.started = true;
      const res = await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ discount: wanted }),
      });
      if (!res.ok) throw Object.assign(new Error('discount_update_failed'), { code: 'discount_update_failed' });
      const next = await res.json().catch(() => null);
      if (!next || typeof next !== 'object') {
        throw Object.assign(new Error('discount_update_unknown'), { code: 'discount_update_unknown' });
      }
      if (attempt) attempt.observed = next;
      cart.state = next;
      bus.emit('exon:cart', cart.state);
      return cart.state;
    },

    // Clear only the effects witnessed in our completed response. A changed
    // application or an uncertain send without a receipt needs cart review.
    async clearAttemptedGiftDiscount(attempt, identity) {
      if (!attempt || !attempt.started || !attempt.observed) return false;
      const expected = discountApplicationKeys(attempt.observed);
      if (!expected || expected.includes('\n')) return false;
      const owned = cart.giftLines(identity, attempt.observed);
      if (owned.length !== 1 || Number(owned[0].quantity) !== 1 ||
          !(owned[0].line_level_discount_allocations || []).some((allocation) =>
            Number(allocation.amount) > 0 && allocation.discount_application &&
            allocation.discount_application.type === 'discount_code' &&
            allocation.discount_application.key === expected)) return false;
      const current = await cart.refresh();
      if (discountApplicationKeys(current) !== expected) return false;
      const res = await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ discount: '' }),
      });
      if (!res.ok) throw new Error('gift_discount_cleanup_failed');
      const next = await res.json();
      if (!next || !Array.isArray(next.items)) throw new Error('gift_discount_cleanup_unknown');
      cart.state = next;
      bus.emit('exon:cart', cart.state);
      return true;
    },
  };

  /* ---------------- overlay manager ---------------- */
  const overlays = (() => {
    const stack = [];
    let scrollY = 0;

    function lock() {
      if (stack.length > 0) return;
      scrollY = window.scrollY;
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.inlineSize = '100%';
    }
    function unlock() {
      if (stack.length > 0) return;
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.inlineSize = '';
      window.scrollTo(0, scrollY);
    }

    document.addEventListener('keydown', (e) => {
      if (e.key !== 'Escape' || stack.length === 0) return;
      const top = stack[stack.length - 1];
      if (top.dismissible !== false) top.close();
    });

    return {
      open(id, { onClose, dismissible = true } = {}) {
        lock();
        const entry = {
          id,
          dismissible,
          returnFocus: document.activeElement,
          close: () => {
            const i = stack.indexOf(entry);
            if (i === -1) return;
            stack.splice(i, 1);
            unlock();
            if (onClose) onClose();
            if (entry.returnFocus && entry.returnFocus.focus) entry.returnFocus.focus();
            bus.emit('exon:overlay:closed', { id });
          },
        };
        stack.push(entry);
        bus.emit('exon:overlay:opened', { id });
        return entry.close;
      },
      closeTop() {
        const top = stack[stack.length - 1];
        if (top) top.close();
      },
      depth() { return stack.length; },
    };
  })();

  /* ---------------- mode toggle ---------------- */
  const mode = {
    get: () => document.documentElement.getAttribute('data-mode') || 'dark',
    set(m) {
      document.documentElement.setAttribute('data-mode', m);
      try { localStorage.setItem('exon:mode', m); } catch (e) { /* private mode */ }
      bus.emit('exon:mode', m);
    },
    toggle() { mode.set(mode.get() === 'dark' ? 'light' : 'dark'); },
  };

  window.EXON = { bus, cart, overlays, mode };
  /* keep the boot refresh promise so consumers can await the first snapshot;
     it never rejects outward (offline first paint is fine). */
  cart.ready = cart.refresh().catch(() => { /* offline first paint is fine */ });
})();
